import React, { useState, useEffect } from 'react';
import Notiflix from 'notiflix';

import {
  IconButton,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { formatDateString, formatJsonForBackend } from '../utils/util';

import './css/DeviceDataView.css';

interface Props {
  data: {
    attributeId: string;
    deviceId: string;
    id: string;
  };
  removeDeviceData: Function;
  authenticated: boolean;
  authenticateUser: () => void;
  fetchProjects: (companyId: string) => Promise<void>;
  companies: any[];
  projects: any[];
  apiKey: string | null;
}

const DeviceDataView = ({
  data,
  removeDeviceData,
  authenticated,
  authenticateUser,
  fetchProjects,
  companies,
  projects,
  apiKey,
}: Props) => {
  const [copied, setCopied] = useState(false);
  const [creationTime] = useState(new Date().toLocaleString());
  const { id, ...rest } = data;

  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [selectedMeasurementDefinition, setSelectedMeasurementDefinition] =
    useState<string | null>(null);
  const [measurementDefinitions, setMeasurementDefinitions] = useState<any[]>(
    []
  );

  useEffect(() => {
    if (selectedCompany) {
      fetchProjects(selectedCompany);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedProject) {
      fetchMeasurementDefinitions(selectedProject);
    }
  }, [selectedProject]);

  const handleCopy = () => {
    setCopied(true);
    if (!copied) {
      const data = formatJsonForBackend(rest);
      navigator.clipboard.writeText(data);

      Notiflix.Notify.success('Copied current data to clipboard!', {
        fontSize: '14px',
      });

      setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
  };

  const fetchMeasurementDefinitions = async (projectId: string) => {
    try {
      const response = await fetch(
        `https://backend.dataskop.at/api/project/measurementdefinitions/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch measurement definitions');
      }

      const definitions = await response.json();
      setMeasurementDefinitions(definitions);
    } catch (error) {
      Notiflix.Notify.failure((error as any).message);
    }
  };

  const handlePost = async () => {
    if (
      !selectedCompany ||
      !selectedProject ||
      !selectedMeasurementDefinition
    ) {
      Notiflix.Notify.failure(
        'Please select a company, project, and measurement definition first.'
      );
      return;
    }

    try {
      const response = await fetch(
        `https://backend.dataskop.at/api/measurementdefinition/update/${selectedMeasurementDefinition}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
          },
          body: formatJsonForBackend(rest),
        }
      );

      if (!response.ok) {
        const errorResponse = await response.json();
        console.error('Error response:', errorResponse);
        throw new Error(
          `Failed to post data: ${errorResponse.message || response.statusText}`
        );
      }

      Notiflix.Notify.success('Data posted successfully!');
    } catch (error) {
      Notiflix.Notify.failure((error as any).message);
    }
  };

  if (!data.attributeId || !data.deviceId) {
    return null;
  }

  return (
    <div id={id} className='object-viewer'>
      <pre className='data-viewer' style={{ backgroundColor: '#cecece' }}>
        {JSON.stringify(rest, null, 2)}
      </pre>
      <div className='ddv-container'>
        <div className='ddv-buttons'>
          <Tooltip title='Delete'>
            <IconButton
              className='ddv-iconBtn'
              onClick={() => removeDeviceData(id)}
              size='small'
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Copy to clipboard'>
            <IconButton
              className='ddv-iconBtn'
              onClick={handleCopy}
              size='small'
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          {!authenticated && (
          <Tooltip title='Post data directly'>
            <IconButton
              className='ddv-iconBtn'
              onClick={authenticateUser}
              size='small'
            >
              <CloudUploadIcon />
            </IconButton>
          </Tooltip>
        )}
        </div>
        <div className='ddv-timestamp'>
          <span className='time-stamp'>{formatDateString(creationTime)}</span>
        </div>
      </div>
      <div className='form-container'>
        {authenticated && (
          <div className='controls'>
            <FormControl
              className='ddv-select'
              variant='outlined'
              margin='dense'
              size='small'
            >
              <InputLabel id='company-select-label'>
                Select a company
              </InputLabel>
              <Select
                labelId='company-select-label'
                id='company-select'
                value={selectedCompany || ''}
                onChange={(e) => setSelectedCompany(e.target.value as string)}
                label='Package Type'
              >
                <MenuItem value=''>
                  <em>Select a company</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    <strong>Id:</strong>&nbsp;{company.id}&nbsp;— &nbsp;
                    <strong>Name:</strong>&nbsp;{company.information.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCompany && (
              <FormControl
                className='ddv-select'
                variant='outlined'
                margin='dense'
                size='small'
              >
                <InputLabel id='project-select-label'>
                  Select a project
                </InputLabel>
                <Select
                  labelId='project-select-label'
                  id='project-select'
                  value={selectedProject || ''}
                  onChange={(e) => setSelectedProject(e.target.value as string)}
                  label='Package Type'
                >
                  <MenuItem value=''>
                    <em>Select a project</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      <strong>Id:</strong>&nbsp;{project.id}&nbsp;— &nbsp;
                      <strong>Name:</strong>&nbsp;{project.information.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedProject && (
              <FormControl
                className='ddv-select'
                variant='outlined'
                margin='dense'
                size='small'
              >
                <InputLabel id='measurement-definition-select-label'>
                  Select a measurement definition
                </InputLabel>
                <Select
                  labelId='measurement-definition-select-label'
                  id='measurement-definition-select'
                  value={selectedMeasurementDefinition || ''}
                  onChange={(e) =>
                    setSelectedMeasurementDefinition(e.target.value as string)
                  }
                  label='Package Type'
                >
                  <MenuItem value=''>
                    <em>Select a measurement definition</em>
                  </MenuItem>
                  {measurementDefinitions.map((def) => (
                    <MenuItem key={def.id} value={def.id}>
                      <strong>Id:</strong>&nbsp;{def.id}&nbsp;— &nbsp;
                      <strong>Name:</strong>&nbsp;{def.information.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedCompany &&
              selectedProject &&
              selectedMeasurementDefinition && (
                <Tooltip title='Post data to database'>
                  <Button
                    onClick={handlePost}
                    color='primary'
                    variant='contained'
                    size='small'
                    className='post-button'
                  >
                    Post Data
                  </Button>
                </Tooltip>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceDataView;
